import type { AdminCmd, AdminData, AdminPlayer } from '../protocol/messages';
import type { PlayerEnt, SkillSlot } from '../sim/types';
import { SKILLS, aspectPath } from '../content/skills';
import { KEYSTONES } from '../content/keystones';
import { ITEMS, type Rarity } from '../content/items';
import { CONSUMABLES } from '../content/consumables';
import { MOBS } from '../content/mobs';
import { BASE_STATS, xpForLevel, MAX_LEVEL, RANK_MAX, rankXpForRank, type Modifier, type StatKey } from '../content/stats';
import { recomputeStats, addXp, playerPower } from '../sim/player';
import { BACKPACK_SLOTS } from '../sim/types';
import { enforceBudget } from '../sim/build';
import { spawnMob } from '../sim/mobs';
import { dealDamage, heal } from '../sim/combat';
import { spawnBoss } from '../sim/realm';
import type { RealmRoom } from './realmRoom';

/**
 * Owner debug console: simulation-level commands run here for both the server and the offline host;
 * account-level commands (fame, unlocks, slots, pets, kicks) are handled by the host itself.
 */
export interface AdminResult {
  ok: boolean;
  msg?: string;
  data?: AdminData;
}

export function adminPlayers(room: RealmRoom): AdminPlayer[] {
  const out: AdminPlayer[] = [];
  for (const c of room.clients.values()) {
    const p = room.world.players.get(c.playerId);
    if (!p) continue;
    out.push({ id: p.id, accountId: p.accountId, name: p.name, hero: p.heroId, level: p.level, rank: p.rank, hp: p.hp, maxHp: p.maxHp, x: Math.round(p.x), y: Math.round(p.y), god: !!p.admin?.god, dead: p.dead });
  }
  return out;
}

export function adminRealmInfo(room: RealmRoom): AdminData['realm'] {
  const st = room.world.realm;
  let mobs = 0;
  for (const m of room.world.mobs.values()) if (!m.dead && m.kind !== 'summon') mobs++;
  return {
    code: room.code,
    corruption: st ? (st.corruption / Math.max(1, st.corruptionNeeded)) * 100 : 0,
    phase: st?.phase ?? '-',
    mobs,
    boss: st?.bossId ? room.world.mobs.get(st.bossId)?.def ?? '' : '',
  };
}

/** Runs a simulation command; returns null for account-level commands the host must handle. */
export function adminSim(room: RealmRoom, sid: number, cmd: AdminCmd): AdminResult | null {
  const world = room.world;
  const client = room.clients.get(sid);
  const self = client ? world.players.get(client.playerId) : undefined;
  const pick = (target?: number): PlayerEnt | undefined => (target !== undefined ? world.players.get(target) : self);
  const info = (): AdminData => ({ players: adminPlayers(room), realm: adminRealmInfo(room) });
  const noTarget = (): AdminResult => ({ ok: false, msg: 'admin: no such player' });
  switch (cmd.k) {
    case 'players':
      return { ok: true, data: info() };
    case 'god': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      p.admin = { god: cmd.on, mods: p.admin?.mods ?? [] };
      p.invulnUntil = cmd.on ? Number.MAX_SAFE_INTEGER : 0;
      return { ok: true, msg: `god ${cmd.on ? 'on' : 'off'}: ${p.name}`, data: info() };
    }
    case 'heal': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      heal(world, p, p.maxHp, null);
      p.hp = p.maxHp;
      return { ok: true, data: info() };
    }
    case 'level': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      const level = Math.max(1, Math.min(MAX_LEVEL, cmd.level));
      p.level = level;
      p.xp = 0;
      p.xpNext = xpForLevel(level);
      p.power = playerPower(level);
      recomputeStats(world, p);
      p.hp = p.maxHp;
      return { ok: true, msg: `${p.name}: level ${level}`, data: info() };
    }
    case 'xp': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      addXp(world, p, cmd.amount);
      return { ok: true, msg: `${p.name}: +${cmd.amount} xp`, data: info() };
    }
    case 'rank': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      p.rank = Math.max(1, Math.min(RANK_MAX, cmd.rank));
      p.rankXp = 0;
      p.rankXpNext = rankXpForRank(p.rank);
      enforceBudget(p);
      recomputeStats(world, p);
      return { ok: true, msg: `${p.name}: rank ${p.rank}`, data: info() };
    }
    case 'skill': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      const def = SKILLS[cmd.id];
      if (!def) return { ok: false, msg: 'admin: no such skill' };
      const level = Math.max(1, Math.min(def.maxLevel, cmd.level ?? 1));
      const aspects = cmd.aspect ? aspectPath(cmd.id, cmd.aspect.split('/').map((a) => a.trim()).filter(Boolean)).map((a) => a.id) : undefined;
      if (cmd.aspect && (!aspects || !aspects.length)) return { ok: false, msg: 'admin: no such aspect path' };
      if (def.kind === 'trait') {
        const tr = p.traits.find((s) => s.id === cmd.id);
        if (tr) tr.level = level;
        else p.traits.push({ id: cmd.id, level });
      } else {
        const list = def.kind === 'active' ? p.actives : p.autos;
        const slot = list.find((s) => s.id === cmd.id);
        if (slot) {
          slot.level = level;
          if (aspects) slot.aspects = aspects;
        } else {
          if (def.kind === 'active' && p.actives.length >= 4) return { ok: false, msg: 'admin: four actives already' };
          const s: SkillSlot = { id: cmd.id, level, aspects: aspects ?? [], cd: 0 };
          list.push(s);
        }
      }
      recomputeStats(world, p);
      return { ok: true, msg: `${p.name}: ${cmd.id} ${level}${aspects?.length ? ' / ' + aspects.join('/') : ''}`, data: info() };
    }
    case 'keystone': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      if (!KEYSTONES[cmd.id]) return { ok: false, msg: 'admin: no such keystone' };
      if (!p.keystones.includes(cmd.id)) p.keystones.push(cmd.id);
      recomputeStats(world, p);
      return { ok: true, msg: `${p.name}: ${cmd.id}`, data: info() };
    }
    case 'item': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      const def = ITEMS[cmd.def];
      if (!def) return { ok: false, msg: 'admin: no such item' };
      const item = { uid: world.allocId(), def: cmd.def, rarity: (def.legendary ? 3 : (cmd.rarity ?? 2)) as Rarity, affixes: [] as Modifier[] };
      // the console equips what it was told to, class restrictions and all; the old piece goes to the bag
      const old = p.equipment[def.slot];
      if (def.slot === 'weapon') p.equipment.weapon = item;
      else p.equipment[def.slot] = item;
      if (old && p.backpack.length < BACKPACK_SLOTS) p.backpack.push(old);
      recomputeStats(world, p);
      p.hp = Math.min(p.hp, p.maxHp);
      return { ok: true, msg: `${p.name}: ${cmd.def}`, data: info() };
    }
    case 'consumable': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      const def = CONSUMABLES[cmd.id];
      if (!def) return { ok: false, msg: 'admin: no such consumable' };
      const n = cmd.n ?? def.maxStack;
      const same = p.consumables.findIndex((c) => c?.id === cmd.id);
      const free = p.consumables.findIndex((c) => c === null);
      if (same >= 0) p.consumables[same] = { id: cmd.id, n: Math.min(def.maxStack, p.consumables[same]!.n + n) };
      else if (free >= 0) p.consumables[free] = { id: cmd.id, n: Math.min(def.maxStack, n) };
      else return { ok: false, msg: 'admin: consumable slots full' };
      return { ok: true, data: info() };
    }
    case 'mods': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      const mods: Modifier[] = [];
      for (const m of cmd.mods) if (m.stat in BASE_STATS) mods.push({ stat: m.stat as StatKey, add: m.add, inc: m.inc, more: m.more });
      p.admin = { god: p.admin?.god ?? false, mods };
      recomputeStats(world, p);
      p.hp = Math.min(p.hp, p.maxHp);
      return { ok: true, msg: `${p.name}: ${mods.length} cheat mods`, data: info() };
    }
    case 'teleport': {
      const p = pick(cmd.target);
      if (!p) return noTarget();
      p.x = cmd.x;
      p.y = cmd.y;
      p.vx = 0;
      p.vy = 0;
      p.knockX = 0;
      p.knockY = 0;
      return { ok: true, data: { ...info(), teleport: p === self ? { x: cmd.x, y: cmd.y } : undefined } };
    }
    case 'slay': {
      const p = world.players.get(cmd.target);
      if (!p || p.dead) return noTarget();
      p.invulnUntil = 0;
      if (p.admin) p.admin.god = false;
      dealDamage(world, self && self !== p ? self : null, p, p.hp * 10 + 1e6, ['void'], {});
      return { ok: true, msg: `${p.name} slain`, data: info() };
    }
    case 'kill': {
      const radius = cmd.radius ?? 6000;
      const ox = self?.x ?? 0;
      const oy = self?.y ?? 0;
      const team = self?.team ?? 1;
      let n = 0;
      for (const m of [...world.mobs.values()]) {
        if (m.dead || m.kind === 'summon' || m.isPet || m.team === team) continue;
        if (Math.hypot(m.x - ox, m.y - oy) > radius) continue;
        dealDamage(world, self ?? null, m, m.hp * 10 + 1e6, ['void'], {});
        n++;
      }
      return { ok: true, msg: `killed ${n}`, data: info() };
    }
    case 'clear': {
      let n = 0;
      for (const m of world.mobs.values()) {
        if (m.dead || m.kind !== 'mob') continue;
        m.dead = true;
        n++;
      }
      return { ok: true, msg: `cleared ${n}`, data: info() };
    }
    case 'spawn': {
      const def = MOBS[cmd.def];
      if (!def) return { ok: false, msg: 'admin: no such mob' };
      if (def.boss) return { ok: false, msg: 'admin: use the boss command' };
      const ax = self ? self.input.ax : 0;
      const ay = self ? self.input.ay : 0;
      for (let i = 0; i < cmd.count; i++) spawnMob(world, cmd.def, ax + world.rng.float(-40, 40), ay + world.rng.float(-40, 40), { elite: cmd.elite });
      return { ok: true, msg: `spawned ${cmd.count} × ${cmd.def}`, data: info() };
    }
    case 'boss': {
      const st = world.realm;
      if (!st) return { ok: false, msg: 'admin: not a realm' };
      if (st.phase !== 'open') return { ok: false, msg: `admin: realm phase is ${st.phase}` };
      spawnBoss(world);
      return { ok: true, msg: 'boss summoned', data: info() };
    }
    case 'corruption': {
      const st = world.realm;
      if (!st) return { ok: false, msg: 'admin: not a realm' };
      st.corruption = (st.corruptionNeeded * cmd.value) / 100;
      return { ok: true, data: info() };
    }
    case 'say': {
      const p = world.players.get(cmd.as);
      if (!p) return noTarget();
      room.broadcast({ t: 'chat', from: p.name, text: cmd.text, id: p.id });
      return { ok: true };
    }
    case 'announce':
      world.events.push({ t: 'announce', key: 'announce.custom', vars: { text: cmd.text }, color: '#ffd166' });
      return { ok: true };
    default:
      return null;
  }
}
